import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import Coin from "../assets/images/coin.svg";
import Cart from "../assets/images/Home/Cart.svg";
import Box from "../assets/images/Home/Box.svg";
import Arrow from "../assets/images/Home/Arrow.svg";
import Wallet from "../assets/images/Home/Wallet.svg";
import Piechart from "../assets/images/Home/Piechart.svg";
import Plus from "../assets/images/Home/Plus.svg";
import { useNavigate } from "react-router-dom";
import PayAsYouGO from "../assets/images/Home.png";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

// Enable custom parse format plugin for dayjs
dayjs.extend(customParseFormat);

// ============================================
// CONSTANTS
// ============================================
const API_URL = process.env.REACT_APP_BASE_URL;
const ITEMS_PER_PAGE = 5;
const MONTHS_TO_SHOW = 4;
const DAYS_TO_FETCH = 120;

// Color scheme for chart - Added refund color
const COLORS = {
  revenue: "#FF5B5B",
  cost: "#9CA3AF",
  profit: "#3B82F6",
  orders: "#CBCFF1",
  walletTopUp: "#10B981",
  refund: "#F59E0B", // Orange/Amber color for refunds
};

// ============================================
// UTILITY FUNCTIONS
// ============================================
const parseCurrencyValue = (value) => {
  if (!value) return 0;
  if (typeof value === "number") return value;
  const cleaned = String(value).replace(/[^\d.-]/g, "").trim();
  return parseFloat(cleaned) || 0;
};

// Parse wallet amount like "250 $" or "276.00 $"
const parseWalletAmount = (amountStr) => {
  if (!amountStr) return 0;
  // Remove $ symbol and any whitespace, then parse as float
  const cleaned = String(amountStr)
    .replace(/\$/g, "")
    .replace(/,/g, "")
    .trim();
  const value = parseFloat(cleaned);
  return isNaN(value) ? 0 : value;
};

// Parse wallet date format "02/19/2026 22:53:46" -> dayjs object
const parseWalletDate = (dateStr) => {
  if (!dateStr) return null;
  
  // Try parsing with the exact format from API
  let parsed = dayjs(dateStr, "MM/DD/YYYY HH:mm:ss");
  
  if (!parsed.isValid()) {
    // Fallback: try other common formats
    parsed = dayjs(dateStr, "MM/DD/YYYY");
  }
  
  if (!parsed.isValid()) {
    // Last resort: let dayjs auto-detect
    parsed = dayjs(dateStr);
  }
  
  return parsed.isValid() ? parsed : null;
};

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good Morning";
  if (hour < 17) return "Good Afternoon";
  return "Good Evening";
};

const filterByRange = (rawData, filterType) => {
  if (!rawData?.length) return [];
  const now = dayjs();

  return rawData.filter((item) => {
    const parsedDate = dayjs(item.purchasedate, "MM/DD/YYYY HH:mm:ss");
    if (!parsedDate.isValid()) return false;

    return filterType === "month"
      ? parsedDate.isSame(now, "month")
      : parsedDate.isAfter(now.subtract(30, "day"));
  });
};

// Create months structure for the last N months - Added refund field
const createMonthsStructure = (monthsCount = 4) => {
  const now = dayjs();
  const months = [];

  for (let i = monthsCount - 1; i >= 0; i--) {
    const monthDate = now.subtract(i, "month");
    months.push({
      month: monthDate.format("MMM"),
      fullMonth: monthDate.format("MMMM YYYY"),
      monthNum: monthDate.month(),
      year: monthDate.year(),
      revenue: 0,
      cost: 0,
      profit: 0,
      orders: 0,
      walletTopUp: 0,
      refund: 0, // Added refund field
    });
  }

  return months;
};

// ============================================
// CUSTOM TOOLTIP COMPONENT - Updated with Refund
// ============================================
const CustomChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const data = payload[0]?.payload;
  if (!data) return null;

  const tooltipItems = [
    { label: "Revenue", value: data.revenue, color: COLORS.revenue, prefix: "$", icon: "💰" },
    { label: "Cost", value: data.cost, color: COLORS.cost, prefix: "$", icon: "💵" },
    { label: "Profit", value: data.profit, color: COLORS.profit, prefix: "$", icon: "📈" },
    { label: "Orders", value: data.orders, color: COLORS.orders, prefix: "", icon: "📦" },
    { label: "Wallet Top Up", value: data.walletTopUp, color: COLORS.walletTopUp, prefix: "$", icon: "💳" },
    { label: "Refund", value: data.refund, color: COLORS.refund, prefix: "$", icon: "🔄" },
  ];

  return (
    <div className="bg-white p-4 border-2 border-[#FF5B5B] rounded-xl shadow-xl min-w-[220px]">
      <p className="font-bold text-lg text-gray-800 mb-3 pb-2 border-b">
        {data.fullMonth || label}
      </p>
      <div className="space-y-2">
        {tooltipItems.map(({ label, value, color, prefix, icon }) => (
          <div key={label} className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-2" style={{ color }}>
              <span
                className="w-3 h-3 rounded"
                style={{ backgroundColor: color }}
              />
              {icon} {label}:
            </span>
            <span className="font-bold" style={{ color }}>
              {prefix}
              {typeof value === "number" ? value.toFixed(2) : value}
            </span>
          </div>
        ))}
      </div>
      {data.revenue > 0 && (
        <div className="mt-3 pt-2 border-t text-center">
          <span className="text-xs text-gray-500">Profit Margin: </span>
          <span className="font-bold text-[#3B82F6]">
            {((data.profit / data.revenue) * 100).toFixed(1)}%
          </span>
        </div>
      )}
    </div>
  );
};

// ============================================
// LEGEND FORMATTER - Updated with Refund
// ============================================
const legendFormatter = (value) => {
  const labels = {
    revenue: "💰 Revenue ($)",
    cost: "💵 Cost ($)",
    profit: "📈 Profit ($)",
    orders: "📦 Orders",
    walletTopUp: "💳 Wallet Top Up ($)",
    refund: "🔄 Refund ($)",
  };
  return (
    <span style={{ color: "#374151", fontWeight: "600", fontSize: "11px" }}>
      {labels[value] || value}
    </span>
  );
};

// ============================================
// SUMMARY CARD COMPONENT
// ============================================
const SummaryCard = ({ icon, label, value, bgColor, textColor, prefix = "" }) => (
  <div className={`text-center ${bgColor} rounded-lg py-3 px-2`}>
    <span className="text-xs text-gray-600 block">
      {icon} {label}
    </span>
    <span className={`text-lg font-bold ${textColor}`}>
      {prefix}
      {typeof value === "number" ? value.toFixed(2) : value}
    </span>
  </div>
);

// ============================================
// DEBUG COMPONENT - Updated with Refund info
// ============================================
const WalletDebugPanel = ({ walletTransactions, refundTransactions, monthlyBreakdown }) => {
  const [showDebug, setShowDebug] = useState(false);

  if (!showDebug) {
    return (
      <button
        onClick={() => setShowDebug(true)}
        className="text-xs text-blue-500 underline mt-2"
      >
        Show Wallet Debug Info
      </button>
    );
  }

  return (
    <div className="mt-4 p-4 bg-gray-50 rounded-lg border text-sm">
      <div className="flex justify-between items-center mb-2">
        <h4 className="font-bold text-gray-700">Wallet Transactions Debug</h4>
        <button
          onClick={() => setShowDebug(false)}
          className="text-red-500 text-xs"
        >
          Close
        </button>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-green-50 p-2 rounded">
          <p className="text-green-600 font-semibold">
            Credit Transactions: {walletTransactions.length}
          </p>
        </div>
        <div className="bg-orange-50 p-2 rounded">
          <p className="text-orange-600 font-semibold">
            Refund Transactions: {refundTransactions.length}
          </p>
        </div>
      </div>
      
      {/* Monthly Breakdown */}
      <div className="mb-4">
        <h5 className="font-semibold text-gray-700 mb-1">Monthly Breakdown:</h5>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {monthlyBreakdown.map((item, idx) => (
            <div key={idx} className="bg-white p-2 rounded border">
              <span className="block font-medium">{item.fullMonth}</span>
              <div className="flex justify-between">
                <span className="text-green-600 text-xs">
                  Top Up: ${item.walletTopUp.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-orange-600 text-xs">
                  Refund: ${item.refund?.toFixed(2) || '0.00'}
                </span>
              </div>
              <span className="text-gray-400 text-xs block">
                ({item.creditCount || 0} credits, {item.refundCount || 0} refunds)
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Credit Transactions */}
      <div className="max-h-40 overflow-y-auto mb-4">
        <h5 className="font-semibold text-green-700 mb-1">Recent Credit Transactions:</h5>
        <table className="w-full text-xs">
          <thead className="bg-green-100">
            <tr>
              <th className="p-1 text-left">Date</th>
              <th className="p-1 text-left">Amount</th>
              <th className="p-1 text-left">Company</th>
            </tr>
          </thead>
          <tbody>
            {walletTransactions.slice(0, 5).map((tx, idx) => (
              <tr key={idx} className="border-b">
                <td className="p-1">{tx.date}</td>
                <td className="p-1 text-green-600">{tx.amount}</td>
                <td className="p-1">{tx.companyName}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Recent Refund Transactions */}
      <div className="max-h-40 overflow-y-auto">
        <h5 className="font-semibold text-orange-700 mb-1">Recent Refund Transactions:</h5>
        <table className="w-full text-xs">
          <thead className="bg-orange-100">
            <tr>
              <th className="p-1 text-left">Date</th>
              <th className="p-1 text-left">Amount</th>
              <th className="p-1 text-left">Description</th>
              <th className="p-1 text-left">Company</th>
            </tr>
          </thead>
          <tbody>
            {refundTransactions.slice(0, 5).map((tx, idx) => (
              <tr key={idx} className="border-b">
                <td className="p-1">{tx.date}</td>
                <td className="p-1 text-orange-600">{tx.amount}</td>
                <td className="p-1">{tx.remarkDescription || tx.description}</td>
                <td className="p-1">{tx.companyName}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const PlanCountRegionwise = () => {
  // Data states
  const [planData, setPlanData] = useState([]);
  const [purchasePlanData, setPurchasePlanData] = useState([]);
  const [walletData, setWalletData] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [refundTransactions, setRefundTransactions] = useState([]); // NEW: Refund transactions state

  // Loading & Error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UI states
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().getMonth() + 1);
  const [currentPage, setCurrentPage] = useState(1);
  const [salesFilter, setSalesFilter] = useState("last30");
  const [ordersFilter, setOrdersFilter] = useState("last30");

  // Auth states
  const [authData, setAuthData] = useState({
    token: null,
    partnerCode: null,
    role: null,
    isAdmin: false,
  });

  // Static values
  const greeting = useMemo(getGreeting, []);
  const navigate = useNavigate();

  // ============================================
  // HANDLERS
  // ============================================
  const handleTopups = useCallback(() => {
    navigate("/AddmoreBalance");
  }, [navigate]);

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  // ============================================
  // INITIALIZE AUTH DATA
  // ============================================
  useEffect(() => {
    const token = localStorage.getItem("token");
    const partnerCode = localStorage.getItem("partnerCode");
    const role = localStorage.getItem("role");

    setAuthData({
      token,
      partnerCode,
      role,
      isAdmin: role === "admin",
    });
  }, []);

  // ============================================
  // FETCH ALL DATA
  // ============================================
  useEffect(() => {
    const { token, partnerCode, isAdmin } = authData;
    if (!token) return;

    const fetchAllData = async () => {
      setLoading(true);
      setError(null);

      // Calculate date ranges
      const now = dayjs();
      const fromDateISO = now.subtract(DAYS_TO_FETCH, "day").format("YYYY-MM-DD");
      const toDateISO = now.format("YYYY-MM-DD");
      const fromDateUS = now.subtract(DAYS_TO_FETCH, "day").format("MM/DD/YYYY");
      const toDateUS = now.format("MM/DD/YYYY");

      // For wallet API - go back 4 months from start of month
      const fromDate4Months = now.subtract(4, "month").startOf("month").format("YYYY-MM-DD");
      // Extend toDate to cover future dates if any (for testing)
      const toDateExtended = now.add(1, "year").format("YYYY-MM-DD");

      const headers = { Authorization: `Bearer ${token}` };

      try {
        // Build API promises
        const apiPromises = [
          // 0: Plan count regionwise
          axios.get(`${API_URL}ESIM/Get_plancount_regionwise?flag=1`, { headers }),

          // 1: Partner purchase plans
          partnerCode &&
            axios.get(
              `${API_URL}ESIM/partner_purchaseplan?partnercode=${partnerCode}&month=${selectedMonth}`,
              { headers }
            ),

          // 2: Wallet data (only for partners)
          partnerCode &&
            axios.get(
              `${API_URL}ESIMGOPartner/get_wallet_invidual?partnercode=${partnerCode}`,
              { headers }
            ),

          // 3: Report data - Admin vs Partner endpoint
          isAdmin
            ? fetch(
                `${API_URL}ESIMGOAdmin/Get_purchasereport_sourcewise?flag=2&fromdate=${fromDateISO}&todate=${toDateISO}`,
                { headers }
              )
            : fetch(
                `${API_URL}ESIM/Get_purchasereport_sourcewise?flag=2&fromdate=${encodeURIComponent(
                  fromDateUS
                )}&todate=${encodeURIComponent(toDateUS)}&partnercode=${partnerCode}`,
                { headers }
              ),

          // 4: Wallet transactions - Credit only (creditOrRefund: 1)
          axios.post(
            `${API_URL}Renew/GetNewWallet`,
            {
              pageIndex: 1,
              pageSize: 500,
              fromDate: fromDate4Months,
              toDate: toDateExtended,
              partnerCode: 0,
              countryCode: "",
              creditOrRefund: 1, // 1 = Credit type only
            },
            {
              headers: {
                ...headers,
                "Content-Type": "application/json",
              },
            }
          ),

          // 5: NEW - Wallet transactions - Refund only (creditOrRefund: 2)
          axios.post(
            `${API_URL}Renew/GetNewWallet`,
            {
              pageIndex: 1,
              pageSize: 500,
              fromDate: fromDate4Months,
              toDate: toDateExtended,
              partnerCode: 0,
              countryCode: "",
              creditOrRefund: 1, // 2 = Refund type only (adjust if API uses different value)
            },
            {
              headers: {
                ...headers,
                "Content-Type": "application/json",
              },
            }
          ),
        ].filter(Boolean);

        const results = await Promise.allSettled(apiPromises);

        // Process Plan Data (index 0)
        if (results[0]?.status === "fulfilled" && results[0].value?.data?.isSuccess) {
          setPlanData(results[0].value.data.planlists || []);
        }

        // Process Purchase Plan Data (index 1)
        if (results[1]?.status === "fulfilled" && results[1].value?.data?.isSuccess) {
          setPurchasePlanData(results[1].value.data.purchseplanlist || []);
        }

        // Process Wallet Data (index 2)
        if (results[2]?.status === "fulfilled" && results[2].value?.data?.getindividualwallet) {
          setWalletData(results[2].value.data.getindividualwallet[0]);
        }

        // Process Report Data (index 3)
        const reportResult = results[3];
        if (reportResult?.status === "fulfilled") {
          const reportResponse = reportResult.value;
          if (reportResponse.ok || reportResponse.data) {
            const data = reportResponse.json
              ? await reportResponse.json()
              : reportResponse.data;
            if (data?.isSuccess) {
              setReportData(data.partnersale || []);
            } else {
              setError(data?.message || "Failed to fetch report data");
            }
          }
        }

        // Process Wallet Credit Transactions (index 4)
        const walletTxResult = results[4];
        if (walletTxResult?.status === "fulfilled") {
          const responseData = walletTxResult.value?.data;
          
          if (responseData?.isSuccess) {
            const walletTxData = responseData.getwallet || [];
            
            // Filter for Credit type only
            const filteredWalletTx = walletTxData.filter(
              (tx) => tx.type?.toLowerCase() === "credit"
            );

            setWalletTransactions(filteredWalletTx);

            console.log("=== CREDIT TRANSACTIONS DEBUG ===");
            console.log("Total from API:", walletTxData.length);
            console.log("Credit only:", filteredWalletTx.length);
          }
        }

        // NEW: Process Wallet Refund Transactions (index 5)
        const refundTxResult = results[5];
        if (refundTxResult?.status === "fulfilled") {
          const responseData = refundTxResult.value?.data;
          
          if (responseData?.isSuccess) {
            const refundTxData = responseData.getwallet || [];
            
            // Filter for Refund type only
            const filteredRefundTx = refundTxData.filter(
              (tx) => tx.type?.toLowerCase() === "refund"
            );

            setRefundTransactions(filteredRefundTx);

            console.log("=== REFUND TRANSACTIONS DEBUG ===");
            console.log("Total from API:", refundTxData.length);
            console.log("Refund only:", filteredRefundTx.length);
            console.log("Sample refunds:", filteredRefundTx.slice(0, 3));
          }
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [authData, selectedMonth]);

  // ============================================
  // COMPUTE GRAPH DATA WITH ALL 6 METRICS (Added Refund)
  // ============================================
  const { graphData, monthlyBreakdown } = useMemo(() => {
    const monthsStructure = createMonthsStructure(MONTHS_TO_SHOW);
    
    // Track transaction counts per month for debugging
    const monthlyBreakdown = monthsStructure.map((m) => ({
      ...m,
      creditCount: 0,
      refundCount: 0,
    }));

    // Aggregate report data by month (Revenue, Cost, Profit, Orders)
    if (reportData?.length) {
      reportData.forEach((item) => {
        const parsedDate = dayjs(item.purchasedate, "MM/DD/YYYY HH:mm:ss");
        if (!parsedDate.isValid()) return;

        const itemMonth = parsedDate.month();
        const itemYear = parsedDate.year();

        const matchingMonth = monthsStructure.find(
          (m) => m.monthNum === itemMonth && m.year === itemYear
        );

        if (matchingMonth) {
          const revenue = parseCurrencyValue(item.purchaseamount);
          const cost = parseCurrencyValue(item.buying_cost);

          matchingMonth.revenue += revenue;
          matchingMonth.cost += cost;
          matchingMonth.profit += revenue - cost;
          matchingMonth.orders += 1;
        }
      });
    }

    // Aggregate wallet Credit transactions by month (Wallet Top Up)
    if (walletTransactions?.length) {
      console.log("=== PROCESSING CREDIT TRANSACTIONS FOR GRAPH ===");
      
      walletTransactions.forEach((tx, index) => {
        const parsedDate = parseWalletDate(tx.date);
        
        if (!parsedDate) {
          console.warn(`Invalid date for credit transaction ${index}:`, tx.date);
          return;
        }

        const txMonth = parsedDate.month();
        const txYear = parsedDate.year();

        const matchingMonthIndex = monthsStructure.findIndex(
          (m) => m.monthNum === txMonth && m.year === txYear
        );

        if (matchingMonthIndex !== -1) {
          const amount = parseWalletAmount(tx.amount);
          
          if (amount > 0) {
            monthsStructure[matchingMonthIndex].walletTopUp += amount;
            monthlyBreakdown[matchingMonthIndex].walletTopUp += amount;
            monthlyBreakdown[matchingMonthIndex].creditCount += 1;
          }
        }
      });
    }

    // NEW: Aggregate wallet Refund transactions by month
    if (refundTransactions?.length) {
      console.log("=== PROCESSING REFUND TRANSACTIONS FOR GRAPH ===");
      
      refundTransactions.forEach((tx, index) => {
        const parsedDate = parseWalletDate(tx.date);
        
        if (!parsedDate) {
          console.warn(`Invalid date for refund transaction ${index}:`, tx.date);
          return;
        }

        const txMonth = parsedDate.month();
        const txYear = parsedDate.year();

        const matchingMonthIndex = monthsStructure.findIndex(
          (m) => m.monthNum === txMonth && m.year === txYear
        );

        if (matchingMonthIndex !== -1) {
          const amount = parseWalletAmount(tx.amount);
          
          if (amount > 0) {
            monthsStructure[matchingMonthIndex].refund += amount;
            monthlyBreakdown[matchingMonthIndex].refund = 
              (monthlyBreakdown[matchingMonthIndex].refund || 0) + amount;
            monthlyBreakdown[matchingMonthIndex].refundCount += 1;
            
            console.log(`Added refund $${amount} to ${monthsStructure[matchingMonthIndex].fullMonth}`);
          }
        } else {
          console.log(`No matching month for refund date: ${tx.date} (${parsedDate.format("MMM YYYY")})`);
        }
      });

      // Log final refund totals
      console.log("=== FINAL REFUND MONTHLY TOTALS ===");
      monthsStructure.forEach((m) => {
        console.log(`${m.fullMonth}: Refund $${m.refund.toFixed(2)}`);
      });
    }

    // Round values for display
    const finalGraphData = monthsStructure.map((month) => ({
      ...month,
      revenue: parseFloat(month.revenue.toFixed(2)),
      cost: parseFloat(month.cost.toFixed(2)),
      profit: parseFloat(month.profit.toFixed(2)),
      orders: month.orders,
      walletTopUp: parseFloat(month.walletTopUp.toFixed(2)),
      refund: parseFloat(month.refund.toFixed(2)),
    }));

    return { 
      graphData: finalGraphData, 
      monthlyBreakdown: monthlyBreakdown.map(m => ({
        ...m,
        walletTopUp: parseFloat(m.walletTopUp.toFixed(2)),
        refund: parseFloat((m.refund || 0).toFixed(2)),
      }))
    };
  }, [reportData, walletTransactions, refundTransactions]);

  // ============================================
  // COMPUTED TOTALS - Updated with Refund
  // ============================================
  const graphTotals = useMemo(() => {
    const totals = graphData.reduce(
      (acc, curr) => ({
        revenue: acc.revenue + curr.revenue,
        cost: acc.cost + curr.cost,
        profit: acc.profit + curr.profit,
        orders: acc.orders + curr.orders,
        walletTopUp: acc.walletTopUp + curr.walletTopUp,
        refund: acc.refund + curr.refund,
      }),
      { revenue: 0, cost: 0, profit: 0, orders: 0, walletTopUp: 0, refund: 0 }
    );

    // Round totals
    return {
      revenue: parseFloat(totals.revenue.toFixed(2)),
      cost: parseFloat(totals.cost.toFixed(2)),
      profit: parseFloat(totals.profit.toFixed(2)),
      orders: totals.orders,
      walletTopUp: parseFloat(totals.walletTopUp.toFixed(2)),
      refund: parseFloat(totals.refund.toFixed(2)),
    };
  }, [graphData]);

  const filteredSalesData = useMemo(
    () => filterByRange(reportData, salesFilter),
    [reportData, salesFilter]
  );

  const filteredOrdersData = useMemo(
    () => filterByRange(reportData, ordersFilter),
    [reportData, ordersFilter]
  );

  const totalRevenue = useMemo(
    () =>
      filteredSalesData.reduce(
        (acc, curr) => acc + parseCurrencyValue(curr.purchaseamount),
        0
      ),
    [filteredSalesData]
  );

  const totalCost = useMemo(
    () =>
      filteredSalesData.reduce(
        (acc, curr) => acc + parseCurrencyValue(curr.buying_cost),
        0
      ),
    [filteredSalesData]
  );

  const totalOrders = filteredOrdersData.length;

  const lastTransaction = useMemo(() => {
    if (!reportData?.length) {
      return { name: "No transactions", amount: "$0.00" };
    }
    return {
      name: reportData[0].planname,
      amount: `$${parseCurrencyValue(reportData[0].purchaseamount).toFixed(2)}`,
    };
  }, [reportData]);

  // Pagination
  const paginatedPlans = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return purchasePlanData.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [purchasePlanData, currentPage]);

  const totalPages = Math.ceil(purchasePlanData.length / ITEMS_PER_PAGE);

  const { role: userRole } = authData;

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="container mx-auto">
      <h1 className="text-[32px] text-[#2D3748] font-bold">{greeting}</h1>

      {/* TOP CARDS ROW */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-[20px] mb-[20px]">
        {/* Card 1: Total Orders */}
        <div className="bg-white rounded-xl p-6 shadow flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="bg-[#CBCFF1] p-4 rounded-full">
              <img src={Cart} alt="Cart" />
            </div>
            <div className="gap-3">
              <h1 className="text-[32px] text-[#383D63] font-medium">
                {totalOrders}
              </h1>
              <p className="text-sm text-gray-600">Total Orders</p>
              <select
                value={ordersFilter}
                onChange={(e) => setOrdersFilter(e.target.value)}
                className="mt-2 p-2 border rounded text-sm"
              >
                <option value="month">This Month</option>
                <option value="last30">Last 30 Days</option>
              </select>
            </div>
          </div>
        </div>

        {/* Card 2: Total Available Plans */}
        <div className="bg-white rounded-xl p-4 shadow flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="bg-[#CBCFF1] p-4 rounded-full relative">
              <img src={Box} alt="Box" />
              <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full" />
            </div>
            <div>
              <h1 className="text-[32px] text-[#383D63] font-medium">357</h1>
              <p className="text-sm text-gray-600">Total Available Plans</p>
            </div>
          </div>
        </div>

        {/* Card 3: Recent Transaction */}
        <div className="bg-white rounded-xl p-4 shadow flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="bg-[#CBCFF1] p-4 rounded-full relative">
              <img src={Arrow} alt="Arrow" />
            </div>
            <div>
              <h1 className="text-[32px] text-[#383D63] font-medium">
                {lastTransaction.amount}
              </h1>
              <p className="text-sm text-gray-600">Recent Transaction</p>
              <p className="text-xs text-gray-400 truncate max-w-[150px]">
                {lastTransaction.name}
              </p>
            </div>
          </div>
        </div>

        {/* Card 4: Total Revenue */}
        <div className="bg-[#383D63] text-white rounded-xl p-4 shadow flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="bg-white p-2 rounded-full">
              <img src={Wallet} alt="Wallet" />
            </div>
            <div>
              <h1 className="text-[32px] text-white font-semibold">
                ${totalRevenue.toFixed(2)}
              </h1>
              <p className="text-sm text-white">Total Revenue</p>
              <select
                value={salesFilter}
                onChange={(e) => setSalesFilter(e.target.value)}
                className="text-sm border rounded px-2 py-1 focus:outline-none text-black"
              >
                <option value="month">This Month</option>
                <option value="last30">Last 30 Days</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* SECOND ROW */}
      <div className="flex flex-wrap items-stretch mb-[20px]">
        {/* Available Regional Plans */}
        <div className="w-full lg:w-1/4 p-2 flex">
          <div className="bg-white shadow rounded-lg p-4 flex-grow flex flex-col">
            <h1 className="text-2xl font-bold mb-4 text-gray-800">
              Top Countries / Regions
            </h1>
            <div className="flex-grow overflow-auto divide-y">
              {planData.slice(0, 6).map((plan, index) => (
                <div
                  key={plan.regionname || index}
                  className="flex justify-between items-center py-3 px-2"
                >
                  <span className="text-gray-700 text-base font-medium">
                    {plan.regionname || "Country Plans"}
                  </span>
                  <span className="text-gray-600 text-sm font-semibold relative flex items-center gap-1">
                    <img src={Piechart} alt="piechart" />
                    {plan.plancount}
                  </span>
                </div>
              ))}
            </div>
            <h4 className="text-center text-semibold text-gray-500 mt-6">
              Top Countries / Regions Purchase Volume
            </h4>
          </div>
        </div>

        {/* CHART WITH 6 BARS */}
        <div className="w-full lg:w-3/4 p-2 flex">
          <div className="border bg-white rounded-lg p-4 w-full flex-grow flex flex-col border-[#FF5B5B]">
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-xl font-bold text-[#2D3748]">
                  Sales Dashboard
                </h1>
                <p className="text-xs text-gray-500 mt-1">
                  Last {MONTHS_TO_SHOW} Months Comparison
                </p>
              </div>
              <div className="flex gap-2">
                <div className="bg-[#D1FAE5] px-3 py-1 rounded-lg">
                  <span className="text-[#10B981] font-semibold text-xs">
                    💳 Top Up: ${graphTotals.walletTopUp.toFixed(2)}
                  </span>
                </div>
                {/* NEW: Refund Badge */}
                <div className="bg-[#FEF3C7] px-3 py-1 rounded-lg">
                  <span className="text-[#F59E0B] font-semibold text-xs">
                    🔄 Refund: ${graphTotals.refund.toFixed(2)}
                  </span>
                </div>
                <div className="bg-[#D9F3EA] px-3 py-1 rounded-lg">
                  <span className="text-[#FF5B5B] font-semibold text-sm">
                    {dayjs().format("YYYY")}
                  </span>
                </div>
              </div>
            </div>

            {/* BAR CHART */}
            <div className="flex-grow">
              {loading ? (
                <div className="flex items-center justify-center h-[300px]">
                  <div className="text-center">
                    <div className="animate-spin text-4xl mb-4">⏳</div>
                    <p className="text-gray-500">Loading chart data...</p>
                  </div>
                </div>
              ) : graphData.some((d) => d.orders > 0 || d.walletTopUp > 0 || d.refund > 0) ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={graphData}
                    margin={{ top: 20, right: 40, left: 10, bottom: 10 }}
                  >
                    <defs>
                      <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={COLORS.revenue} stopOpacity={1} />
                        <stop offset="100%" stopColor={COLORS.revenue} stopOpacity={0.7} />
                      </linearGradient>
                      <linearGradient id="costGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={COLORS.cost} stopOpacity={1} />
                        <stop offset="100%" stopColor={COLORS.cost} stopOpacity={0.7} />
                      </linearGradient>
                      <linearGradient id="profitGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={COLORS.profit} stopOpacity={1} />
                        <stop offset="100%" stopColor={COLORS.profit} stopOpacity={0.7} />
                      </linearGradient>
                      <linearGradient id="ordersGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={COLORS.orders} stopOpacity={1} />
                        <stop offset="100%" stopColor={COLORS.orders} stopOpacity={0.7} />
                      </linearGradient>
                      <linearGradient id="walletTopUpGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={COLORS.walletTopUp} stopOpacity={1} />
                        <stop offset="100%" stopColor={COLORS.walletTopUp} stopOpacity={0.7} />
                      </linearGradient>
                      {/* NEW: Refund Gradient */}
                      <linearGradient id="refundGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={COLORS.refund} stopOpacity={1} />
                        <stop offset="100%" stopColor={COLORS.refund} stopOpacity={0.7} />
                      </linearGradient>
                    </defs>

                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#E5E7EB"
                    />

                    <XAxis
                      dataKey="month"
                      tick={{ fill: "#374151", fontSize: 12, fontWeight: "600" }}
                      axisLine={{ stroke: "#D1D5DB" }}
                      tickLine={false}
                    />

                    <YAxis
                      yAxisId="left"
                      orientation="left"
                      tick={{ fill: COLORS.revenue, fontSize: 11 }}
                      axisLine={{ stroke: COLORS.revenue }}
                      tickLine={false}
                      tickFormatter={(value) => `$${value}`}
                      width={60}
                    />

                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tick={{ fill: COLORS.orders, fontSize: 11 }}
                      axisLine={{ stroke: COLORS.orders }}
                      tickLine={false}
                      width={40}
                    />

                    <Tooltip
                      content={<CustomChartTooltip />}
                      cursor={{ fill: "rgba(0, 176, 116, 0.1)" }}
                    />

                    <Legend
                      wrapperStyle={{ paddingTop: "5px" }}
                      formatter={legendFormatter}
                      iconType="rect"
                      iconSize={10}
                    />

                    <Bar
                      yAxisId="left"
                      dataKey="revenue"
                      name="revenue"
                      fill="url(#revenueGradient)"
                      radius={[4, 4, 0, 0]}
                      barSize={14}
                    />

                    <Bar
                      yAxisId="left"
                      dataKey="cost"
                      name="cost"
                      fill="url(#costGradient)"
                      radius={[4, 4, 0, 0]}
                      barSize={14}
                    />

                    <Bar
                      yAxisId="left"
                      dataKey="profit"
                      name="profit"
                      fill="url(#profitGradient)"
                      radius={[4, 4, 0, 0]}
                      barSize={14}
                    />

                    <Bar
                      yAxisId="right"
                      dataKey="orders"
                      name="orders"
                      fill="url(#ordersGradient)"
                      radius={[4, 4, 0, 0]}
                      barSize={14}
                    />

                    <Bar
                      yAxisId="left"
                      dataKey="walletTopUp"
                      name="walletTopUp"
                      fill="url(#walletTopUpGradient)"
                      radius={[4, 4, 0, 0]}
                      barSize={14}
                    />

                    {/* NEW: Refund Bar */}
                    <Bar
                      yAxisId="left"
                      dataKey="refund"
                      name="refund"
                      fill="url(#refundGradient)"
                      radius={[4, 4, 0, 0]}
                      barSize={14}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[300px]">
                  <div className="text-center">
                    <div className="text-6xl mb-4">📊</div>
                    <p className="text-gray-500 text-lg">No data available</p>
                  </div>
                </div>
              )}
            </div>

            {/* Summary Footer - 6 Metrics (Added Refund) */}
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-2 mt-3 pt-3 border-t border-gray-200">
              <SummaryCard
                icon="💰"
                label="Revenue"
                value={graphTotals.revenue}
                bgColor="bg-[#FFE5E5]"
                textColor="text-[#FF5B5B]"
                prefix="$"
              />
              <SummaryCard
                icon="💵"
                label="Cost"
                value={graphTotals.cost}
                bgColor="bg-[#F3F4F6]"
                textColor="text-[#6B7280]"
                prefix="$"
              />
              <SummaryCard
                icon="📈"
                label="Profit"
                value={graphTotals.profit}
                bgColor="bg-[#DBEAFE]"
                textColor="text-[#3B82F6]"
                prefix="$"
              />
              <SummaryCard
                icon="📦"
                label="Orders"
                value={graphTotals.orders}
                bgColor="bg-[#E0E7FF]"
                textColor="text-[#6366F1]"
              />
              <SummaryCard
                icon="💳"
                label="Top Up"
                value={graphTotals.walletTopUp}
                bgColor="bg-[#D1FAE5]"
                textColor="text-[#10B981]"
                prefix="$"
              />
              {/* NEW: Refund Summary Card */}
              <SummaryCard
                icon="🔄"
                label="Refund"
                value={graphTotals.refund}
                bgColor="bg-[#FEF3C7]"
                textColor="text-[#F59E0B]"
                prefix="$"
              />
            </div>

            {/* Profit Margin Indicator */}
            {graphTotals.revenue > 0 && (
              <div className="mt-2 text-center">
                <span className="text-xs text-gray-500">
                  Overall Profit Margin:{" "}
                </span>
                <span
                  className={`font-bold ${
                    graphTotals.profit >= 0 ? "text-[#3B82F6]" : "text-red-500"
                  }`}
                >
                  {((graphTotals.profit / graphTotals.revenue) * 100).toFixed(1)}%
                </span>
                {graphTotals.refund > 0 && (
                  <span className="ml-4 text-xs text-gray-500">
                    | Refund Rate:{" "}
                    <span className="font-bold text-[#F59E0B]">
                      {((graphTotals.refund / graphTotals.revenue) * 100).toFixed(1)}%
                    </span>
                  </span>
                )}
              </div>
            )}

            {/* Debug Panel - Updated with Refund */}
            <WalletDebugPanel
              walletTransactions={walletTransactions}
              refundTransactions={refundTransactions}
              monthlyBreakdown={monthlyBreakdown}
            />
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <strong>Error:</strong> {error}
        </div>
      )}
    </div>
  );
};

export default PlanCountRegionwise;